import { useRef, useState } from "react";
import { FileText, Loader2, Receipt, Upload } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { ReceiptPreview } from "@/components/ReceiptPreview";
import { api, formatApiError } from "@/lib/api";

const FilePicker = ({ file, onPick }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const isImg = file?.type?.startsWith("image/");
  return (
    <button type="button" onClick={() => ref.current?.click()} data-testid="receipt-dropzone" className="flex min-h-[150px] w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-border bg-muted/40 p-3 transition-colors hover:border-primary/50">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" data-testid="receipt-upload-input" onChange={(e) => onPick(e.target.files?.[0] || null)} />
      {isImg && <img src={URL.createObjectURL(file)} alt="receipt preview" data-testid="receipt-local-preview" className="max-h-56 object-contain" />}
      {file && !isImg && <span className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-5 w-5 text-primary" />{file.name}</span>}
      {!file && <span className="flex flex-col items-center gap-2 text-center text-sm text-muted-foreground"><Upload className="h-7 w-7 text-primary" />{t("upload_receipt_hint")}</span>}
    </button>
  );
};

export const ReceiptUpload = ({ user }) => {
  const { t } = useLang();
  const { setUser } = useAuth();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const hasReceipt = !!user.payment_receipt_path;

  const submit = async () => {
    if (!file) return toast.error(t("err_receipt"));
    const fd = new FormData();
    fd.append("receipt", file);
    setBusy(true);
    try {
      const { data } = await api.post("/me/payment-receipt", fd);
      setUser(data);
      setFile(null);
      toast.success(t("receipt_uploaded"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="dashboard-receipt-section">
      <h2 className="flex items-center gap-2 text-lg font-bold"><Receipt className="h-5 w-5 text-primary" />{hasReceipt ? t("receipt_uploaded") : t("upload_receipt_title")}</h2>
      {hasReceipt && <ReceiptPreview path={user.payment_receipt_path} type={user.payment_receipt_type} testId="dashboard-receipt-preview" className="max-h-56 rounded-xl border border-border object-contain" />}
      <FilePicker file={file} onPick={setFile} />
      <button type="button" onClick={submit} disabled={busy || !file} data-testid="receipt-submit-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground transition-transform hover:scale-[1.01] disabled:opacity-50 sm:w-auto">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{hasReceipt ? t("reupload_receipt") : t("submit_receipt")}
      </button>
    </div>
  );
};
